import { DocumentNode, useMutation } from '@apollo/client';
import { FORM_ERROR } from 'final-form';

import { ADD_SESSION_NOTE_MUTATION, GET_SESSION_NOTES, GET_STUDENT_NOTES, ADD_STUDENT_NOTE_MUTATION } from "ft-shared-graphql"
import { useSlideInContext } from 'ui/SlideIn';
import { ApiUnknownError, ApiValidationError, FormSubmitCallback, mapApiFieldValidationErrors } from 'ui/Form';
import {
    AddSessionNoteMutation,
    AddSessionNoteMutationVariables,
    AddStudentNoteMutation, AddStudentNoteMutationVariables, NoteOriginType, NoteTypeEnum
} from 'ft-shared-graphql';
import NoteForm, { NoteFormProps } from '../../Forms/NoteForm';
import { useParams } from 'react-router';

export const formatBody = (value: string) => {
    return value
        .replace(/_/g, ' ') // Replace underscores with spaces
        .replace(/\b\w/g, (match) => match.toUpperCase()) // Capitalize first letter of each word
        .replace(/\B\w/g, (match) => match.toLowerCase()); // Lowercase the remaining characters
}

export type AddNoteFieldProps = {
    sessionId?: string | undefined,
    studentId?: string | undefined
}

export default function AddNote({ sessionId, studentId }: AddNoteFieldProps) {
    const [addSessionNoteMutation] = useMutation<AddSessionNoteMutation, AddSessionNoteMutationVariables>(ADD_SESSION_NOTE_MUTATION);
    const [addStudentNoteMutation] = useMutation<AddStudentNoteMutation, AddStudentNoteMutationVariables>(ADD_STUDENT_NOTE_MUTATION);
    const { toggleDrawer } = useSlideInContext();
    const params = useParams()
    const isSessionSpecific = (window.location.pathname.includes("notes") && !window.location.pathname.includes("children")) || (window.location.pathname.includes("session"))
    const noteType = isSessionSpecific ? NoteTypeEnum.SessionNote : NoteTypeEnum.General
    const noteOrigin = isSessionSpecific ? window.location.pathname.includes("notes") ? NoteOriginType.HistoryTable : NoteOriginType.ResultsTable : NoteOriginType.NotesTable
    const queriesToRefetch: { query: DocumentNode; variable?: any }[] = [
        sessionId ? {
            query: GET_SESSION_NOTES,
            variables: {
                sessionId: sessionId
            }
        } : null,
        studentId ? {
            query: GET_STUDENT_NOTES,
            variables: {
                studentId: studentId
            }
        } : null].filter(Boolean) as { query: DocumentNode; variables: any }[];

    const handleAddNote: FormSubmitCallback<NoteFormProps> = async (values: any): Promise<ReturnType<FormSubmitCallback>> => {
        if (isSessionSpecific) {
            const response = await addSessionNoteMutation({
                variables: {
                    sessionId: sessionId,
                    type: noteType,
                    origin: noteOrigin,
                    body: values.note
                },
                refetchQueries: queriesToRefetch
            })

            if (response.data?.addSessionNote.errors) {
                const validationError = response.data.addSessionNote.errors
                    .find((e: any) => e.__typename === "ValidationError") as ApiValidationError;

                if (validationError.errors) {
                    const fieldErrors = validationError
                        ? mapApiFieldValidationErrors(validationError)
                        : null;

                    return fieldErrors;
                }

                const unknownError = response.data.addSessionNote.errors
                    .find((e: any) => e.__typename === 'UnknownError') as ApiUnknownError;

                if (unknownError) {
                    return { [FORM_ERROR]: unknownError.message }
                }
            }
            toggleDrawer();
            return undefined
        } else {
            const response = await addStudentNoteMutation({
                variables: {
                    studentId: studentId ?? params.sid,
                    type: noteType,
                    origin: noteOrigin,
                    body: values.note
                },
                refetchQueries: [{
                    query: GET_STUDENT_NOTES,
                    variables: {
                        studentId: studentId
                    }
                }]
            })

            if (response.data?.addStudentNote.errors) {
                const validationError = response.data.addStudentNote.errors
                    .find((e: any) => e.__typename === "ValidationError") as ApiValidationError;

                if (validationError.errors) {
                    const fieldErrors = validationError
                        ? mapApiFieldValidationErrors(validationError)
                        : null;

                    return fieldErrors;
                }

                const unknownError = response.data.addStudentNote.errors
                    .find((e: any) => e.__typename === 'UnknownError') as ApiUnknownError;

                if (unknownError) {
                    return { [FORM_ERROR]: unknownError.message }
                }
            }
            toggleDrawer();
            return undefined

        }


    }

    return (
        <div>
            <NoteForm
                onCancel={() => toggleDrawer()}
                onSubmit={handleAddNote}
            />
        </div>
    )
}
